import React, { useEffect, useRef, useState } from "react";

/**
 * Reveals children with a subtle fade-up the first time they enter the viewport.
 * - If the element is already in the viewport at mount, it shows immediately (no flash).
 * - Honors prefers-reduced-motion (CSS handles fallback).
 */
export default function Reveal({ as: Tag = "div", delay = 0, className = "", children, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Immediate show if already on screen (or close to it) — prevents blank flashes
    const inView = () => {
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 1.1 && r.bottom > 0;
    };
    if (inView()) {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "0px 0px -5% 0px", threshold: 0.01 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
